import { useEffect, useState } from 'react';
import { tokens } from '../forms/tokens';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatMoney } from '../../lib/datetime';
import { cadenceText, commitmentStatusLabel, completionLine } from '../../lib/commitments.helpers';
import commitmentsService from '../../services/commitments.service';
import type { Commitment } from '../../types/commitments';

/**
 * Client-facing widget rendered on /client/portfolio. Lists every
 * commitment the client has signed (or is in the middle of signing),
 * with cadence + upcoming-due summary. The action surface is
 * deliberately small — full lifecycle (signing, cancel, etc.) lives in
 * the existing /client/contracts/{caseId} screen which the user
 * navigates into from the call-to-action link.
 */
export default function MyCommitmentsCard(): JSX.Element | null {
  const [items, setItems] = useState<Commitment[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await commitmentsService.listMine();
        if (!cancelled) setItems(list);
      } catch (e) {
        if (!cancelled) setErr(getApiErrorMessage(e, 'Could not load commitments'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (err) {
    return (
      <div style={cardStyle}>
        <h3 style={h3Style}>My commitments</h3>
        <p style={{ color: tokens.errorText }}>{err}</p>
      </div>
    );
  }

  if (items === null) {
    return (
      <div style={cardStyle}>
        <h3 style={h3Style}>My commitments</h3>
        <p style={{ color: tokens.textMuted }}>Loading…</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div style={cardStyle}>
        <h3 style={h3Style}>My commitments</h3>
        <p style={{ color: tokens.textMuted, fontSize: 13, marginBottom: 12 }}>
          You haven&apos;t set up a contribution plan yet. You can set one up yourself, or send a
          transfer whenever you choose.
        </p>
        <a href="/client/fund#plan" style={{ color: tokens.gold, fontSize: 13, fontWeight: 600 }}>
          Set up a contribution plan →
        </a>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <h3 style={h3Style}>My commitments</h3>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {items.map((c) => (
          <li key={c.id} style={itemStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <strong>{c.product_name}</strong>
                <div style={{ color: tokens.textMuted, fontSize: 12 }}>
                  {cadenceText(c)} · {formatMoney(c.amount, c.currency_code)}
                </div>
                {/* Completion-count line. Bounded commitments (end_date
                    set) show "N of M fulfilled"; open-ended ones show
                    "N fulfilled" — no progress bar implying a finish
                    line, no streaks, no loss-aversion language. */}
                {(c.fulfilled_count > 0 || c.expected_count > 0) && (
                  <div style={{ color: tokens.textMuted, fontSize: 12, marginTop: 2 }}>
                    {completionLine(c)}
                  </div>
                )}
              </div>
              <span style={statusPill(c.status)}>{commitmentStatusLabel(c.status)}</span>
            </div>
            {c.contract_case_id && (
              <div style={{ marginTop: 8 }}>
                <a
                  href={`/client/contracts/${c.contract_case_id}`}
                  style={{ color: tokens.gold, fontSize: 13 }}
                >
                  {c.status === 'drafting' ? 'Open contract to sign →' : 'View commitment details →'}
                </a>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 12,
  padding: 20,
  color: tokens.textPrimary,
};
const h3Style: React.CSSProperties = { margin: '0 0 12px 0', fontSize: 14 };
const itemStyle: React.CSSProperties = {
  padding: '12px 0',
  borderBottom: `1px solid ${tokens.border}`,
};

function statusPill(status: string): React.CSSProperties {
  const palette: Record<string, [string, string]> = {
    drafting: [tokens.infoBg, tokens.infoBorder],
    pending_manager: [tokens.successBg, tokens.successBorder],
    active: [tokens.successBg, tokens.successText],
    paused: [tokens.errorBg, tokens.errorBorder],
    cancelled: [tokens.errorBg, tokens.errorText],
    completed: [tokens.surfaceMuted, tokens.border],
  };
  const [bg, border] = palette[status] ?? [tokens.surfaceMuted, tokens.border];
  return {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 999,
    background: bg,
    border: `1px solid ${border}`,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  };
}
